import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { RunningNotice } from '@/components/RunningNotice';
import { Link } from 'react-router';
import { useAuth } from 'react-oidc-context';
import { apiFetch } from '@/lib/api';
import { waitingForMessage, type AnalysisRun } from '@/lib/analysisGate';
import { formatDuration } from '@/lib/time';
import { canImportData, useMe } from '@/lib/useMe';
import { useActiveDatabase } from '@/lib/useDatabases';
import { cn } from '@/lib/utils';
import { describeApiError } from '@/lib/apiErrors';

/** Mirror of the substrate's `DataReviewReport`. */
interface DataReviewFact {
  subject: string;
  predicate: string;
  object: string;
}
interface DataReviewEntry {
  kind: string;
  severity: string;
  falsifying: boolean;
  what: string;
  entities: string[];
  suggestion?: string | null;
  facts: DataReviewFact[];
}
interface DataReviewReport {
  supported: boolean;
  facts_checked: number;
  facts_contradicted: number;
  entries_total: number;
  by_kind: Record<string, number>;
  entries: DataReviewEntry[];
  capped: boolean;
  /** The label of each entity the entries name, where the store holds one. */
  labels?: Record<string, string>;
}
/** Mirror of the server's review status: the running run, the kept report, a later failure. */
interface ReviewRun {
  started_at: string;
  started_by: string;
}
interface KeptReview extends ReviewRun {
  finished_at: string;
  duration_ms: number;
  statements: number;
  peak_bytes: number | null;
  report: DataReviewReport;
}
interface FailedReview extends ReviewRun {
  finished_at: string | null;
  duration_ms: number | null;
  error: string;
  detail?: string | null;
}
interface ReviewStatus {
  running: ReviewRun | null;
  last: KeptReview | null;
  last_failure: FailedReview | null;
  /** The other whole-store analysis the running review waits behind. */
  waiting_for: AnalysisRun | null;
}

/**
 * Plain-language framing per review kind: what this group of items means and
 * both fix directions. Every mismatch is two-sided — the data can be wrong, or
 * the definitions can be too narrow — so both paths are always shown and the
 * user decides which side is right.
 */
const KIND_GUIDE: Record<string, { title: string; explain: string; fixData: string; fixSchema: string }> = {
  'domain-range': {
    title: 'A property is used on the wrong kind of thing',
    explain:
      'Your ontology says what kind of entity each property applies to, and what kind of value it takes. These facts use a property outside those definitions.',
    fixData: 'If the fact is wrong, correct or remove it on the entity’s page.',
    fixSchema:
      'If the fact is right, the definition is too narrow — add the missing type to the entity, or widen the property’s applicable kinds in the Ontology Editor.',
  },
  'citation-resolves': {
    title: 'A fact cites a document section that is no longer in the store',
    explain:
      'These facts carry a citation to a document — usually a section of a law, regulation or manual in your catalog — and that section is not there. Documents are re-loaded and renumbered; a citation to a section that moved or was dropped points at nothing, and the fact it supports cannot be checked against its source.',
    fixData: 'Point the citation at the section that replaced it, on the fact’s entity page.',
    fixSchema:
      'If the document was re-loaded, load the catalog file that defines the missing section again; the citation resolves as soon as the section exists.',
  },
  'declared-disjointness': {
    title: 'An entity is two things your ontology says cannot go together',
    explain:
      'Your ontology has a rule that nothing can be both of two classes, and these entities are typed with both. Each item names the two types, the rule they break and the reason the rule gives.',
    fixData: 'If one type is wrong, remove it on the entity’s page.',
    fixSchema: 'If both types are right, the rule is wrong: remove it in the Ontology Editor.',
  },
  'max-cardinality': {
    title: 'More values than the definition allows',
    explain:
      'Your ontology limits how many values these properties may carry, and these entities exceed the limit.',
    fixData: 'If the extra values are wrong, remove them on the entity’s page.',
    fixSchema: 'If they are all legitimate, raise or remove the limit in the Ontology Editor.',
  },
  'temporal-consistency': {
    title: 'A fact’s validity window is impossible',
    explain: 'These facts end before they begin — the valid-from date is after the valid-until date.',
    fixData: 'Correct the dates on the fact.',
    fixSchema: 'There is no definition-side fix for an impossible window; the dates themselves need attention.',
  },
  'tag-contract': {
    title: 'A fact is missing recommended context',
    explain:
      'Your ontology recommends context (such as a source or a validity period) for facts like these, and it is absent. The facts still count — they could carry more.',
    fixData: 'Add the missing context to the facts where you know it.',
    fixSchema: 'If the recommendation does not fit this data, relax it in the Ontology Editor.',
  },
  classification: {
    title: 'The index reads an entity differently than its definitions suggest',
    explain:
      'The knowledge index classifies every entity from your ontology — class, instance, property. These entities failed one of its consistency checks: a data type or property standing where a class should be, a literal recorded as an entity, a class with no anchor in the hierarchy, or a classification that changed between builds. Each is usually a missing or ambiguous definition.',
    fixData: 'If the entity is typed wrongly, correct its type on the entity’s page.',
    fixSchema:
      'If the type is right, the definition is ambiguous — anchor the class, or declare the property’s domain and range, in the Ontology Editor.',
  },
  'required-property': {
    title: 'An entity is missing something its definition expects',
    explain: 'These entities lack a property their type declares as expected. They still count — they could be more complete.',
    fixData: 'Fill in the missing values on the entity pages.',
    fixSchema: 'If the property should not be expected for this type, relax the definition in the Ontology Editor.',
  },
};

const FALLBACK_GUIDE = {
  title: 'Other review items',
  explain: 'Places where the data and your definitions disagree in a less common way.',
  fixData: 'If the data is wrong, correct it on the entity’s page.',
  fixSchema: 'If the data is right, adjust the definition in the Ontology Editor.',
};

function guideFor(kind: string) {
  return KIND_GUIDE[kind] ?? { ...FALLBACK_GUIDE, title: `${FALLBACK_GUIDE.title} (${kind})` };
}

/** An entity named by its label when the store holds one, linking its page,
 *  where the user can act on it (23.99: an opaque id with nothing to press
 *  gave the user nothing to do). The id stays as the link's title. */
function EntityLink({ term, label }: { term: string; label?: string | undefined }) {
  // Only identifier-shaped terms link; literal values render as text.
  const linkable = term.includes(':') && !term.includes(' ');
  if (!linkable) return <span className="font-mono text-xs">{term}</span>;
  return (
    <Link
      to={`/entity?uri=${encodeURIComponent(term)}`}
      title={term}
      className={cn('text-xs text-primary hover:underline', label ? 'font-medium' : 'font-mono')}
    >
      {label ?? term}
    </Link>
  );
}

function when(iso: string): string {
  return new Date(iso).toLocaleString();
}

/**
 * Troubleshoot — Data Management's data-review tab. Catalogs every place the
 * loaded data and the ontology's definitions disagree, in plain language,
 * grouped by what kind of disagreement it is, with both fix directions on
 * every group. Opening the tab runs the review (ruled 2026-10-13: old results
 * are never useful in a troubleshooter): a report kept from an earlier visit
 * is not shown, the review starts as a background job, and the report drawn
 * is the one that finishes after the tab was opened.
 */
export function TroubleshootPanel() {
  const auth = useAuth();
  const token = auth.user?.id_token;
  const me = useMe();
  const canRun = auth.isAuthenticated && canImportData(me.data);
  const queryClient = useQueryClient();
  // A review belongs to the database it ran on; one that finishes after a
  // switch lands under its own key, never on the page of the new one.
  const database = useActiveDatabase().active ?? '';
  const [openedAt] = useState(() => Date.now());
  const startedHere = useRef(false);
  const status = useQuery({
    queryKey: ['data-review', database],
    queryFn: async () => {
      const r = await apiFetch<ReviewStatus>('/api/v1/data-review', token);
      if (!r.ok) throw new Error(r.message);
      return r.data;
    },
    refetchInterval: (q) => (q.state.data?.running ? 3000 : false),
  });
  const start = useMutation({
    mutationFn: async () => {
      const r = await apiFetch<ReviewStatus>('/api/v1/data-review/run', token, { method: 'POST' });
      if (!r.ok) throw new Error(r.message);
      return r.data;
    },
    onSuccess: (data) => queryClient.setQueryData(['data-review', database], data),
    onSettled: () => void queryClient.invalidateQueries({ queryKey: ['data-review', database] }),
  });
  // The visit runs the review once, unless one is already running.
  const loaded = status.data;
  useEffect(() => {
    if (!loaded || !canRun || startedHere.current) return;
    startedHere.current = true;
    if (!loaded.running) start.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded, canRun]);

  if (status.isLoading) {
    return <p className="mt-6 text-sm text-muted-foreground">Starting the review…</p>;
  }
  if (status.isError) {
    return (
      <p className="mt-6 text-sm text-tres-error">
        The review status could not be read: {String((status.error as Error).message)}
      </p>
    );
  }
  const s = status.data;
  if (!s) return null;
  const running = s.running;
  // Only a review that finished after this visit opened is shown; a kept one
  // from an earlier visit, or an earlier engine, is never a troubleshooter's
  // answer about the database as it stands.
  const last = s.last && Date.parse(s.last.finished_at) >= openedAt ? s.last : null;
  const earlier = s.last && !last ? s.last : null;
  const failure = s.last_failure && Date.parse(s.last_failure.started_at) >= openedAt ? s.last_failure : null;

  const controls = (
    <div className="mt-6 flex flex-wrap items-start gap-4 rounded-md border border-border bg-card p-4">
      {canRun && (
        <button
          type="button"
          onClick={() => start.mutate()}
          disabled={running !== null || start.isPending}
          className={cn(
            'rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground',
            'hover:bg-primary/90 disabled:opacity-50',
          )}
        >
          Run again
        </button>
      )}
      <div className="min-w-0 flex-1 space-y-1 text-xs text-tres-text-secondary">
        {running && s.waiting_for ? (
          <p>
            {running.started_by} asked for a review at {when(running.started_at)}.{' '}
            {waitingForMessage(s.waiting_for, when)} The review starts as soon as it is done, and
            you can leave this page meanwhile.
          </p>
        ) : running ? (
          <>
            <p>
              {running.started_by} started a review at {when(running.started_at)}. It checks the
              whole database against your ontology’s definitions; the report appears here when it
              finishes, and you can leave this page meanwhile.
            </p>
            <RunningNotice since={Date.parse(running.started_at)} what="data review" />
          </>
        ) : last ? (
          <p>
            Reviewed {when(last.finished_at)} by {last.started_by} · took{' '}
            {formatDuration(last.duration_ms / 1000)} · {last.statements.toLocaleString()} facts
            checked.
          </p>
        ) : canRun ? (
          <p>The review starts when this tab opens and checks every fact against the ontology.</p>
        ) : (
          <p>
            A review runs when a Power User or Administrator opens this tab.
            {earlier ? ` The last one finished ${when(earlier.finished_at)}; its report is not kept here.` : ''}
          </p>
        )}
        {failure && (
          <p className="text-tres-error">
            The review {failure.started_by} started at {when(failure.started_at)}{' '}
            {failure.error === 'the review did not finish' ? `did not finish.${failure.detail ? ` ${failure.detail}` : ''}` : `stopped. ${describeApiError(500, failure.error, failure.detail)}`}
            {last ? ' The report below is from the last review that finished.' : ''}
          </p>
        )}
        {start.isError && (
          <p className="text-tres-error">{String((start.error as Error).message)}</p>
        )}
      </div>
    </div>
  );

  if (!last) return controls;
  const m = last.report;

  if (!m.supported) {
    return (
      <>
        {controls}
        <p className="mt-6 text-sm text-muted-foreground">
          This database checks data against its definitions at import time, so there is no
          whole-database review to show here. Review runs on QROS-backed instances.
        </p>
      </>
    );
  }

  const mismatched = m.entries.filter((e) => e.falsifying);
  const advisory = m.entries.filter((e) => !e.falsifying);

  return (
    <div className="space-y-6">
      {controls}
      <div className="rounded-md border border-border bg-card p-4">
        <p className="text-sm text-tres-text-primary">
          Checked <span className="font-semibold">{m.facts_checked.toLocaleString()}</span> facts
          against the ontology.{' '}
          {m.facts_contradicted > 0 ? (
            <>
              <span className="font-semibold text-tres-error">{m.facts_contradicted.toLocaleString()}</span>{' '}
              do not fit it, in <span className="font-semibold">{m.entries_total.toLocaleString()}</span>{' '}
              item{m.entries_total === 1 ? '' : 's'}.
            </>
          ) : m.entries_total > 0 ? (
            <>
              All of them fit it; <span className="font-semibold">{m.entries_total.toLocaleString()}</span>{' '}
              item{m.entries_total === 1 ? '' : 's'} could be more complete.
            </>
          ) : (
            <>All of them fit it.</>
          )}
        </p>
        <p className="mt-1 text-xs text-muted-foreground">
          Every fact is in the database. For each item, either the fact is wrong or the ontology
          is too narrow; you decide which.
          {m.capped ? ` The first ${m.entries.length.toLocaleString()} items are listed; the counts cover the whole database.` : ''}
        </p>
      </div>

      {m.entries_total === 0 && (
        <p className="text-sm text-muted-foreground">
          Nothing to review — the data and the definitions agree everywhere.
        </p>
      )}

      {[
        { label: 'Facts that do not match the definitions', list: mismatched, tone: 'error' },
        { label: 'Facts that could be more complete', list: advisory, tone: 'muted' },
      ].map(
        ({ label, list, tone }) =>
          list.length > 0 && (
            <section key={label}>
              <h2 className="mb-2 text-base font-semibold text-tres-text-primary">{label}</h2>
              {groupByKind(list).map(([kind, entries]) => {
                const guide = guideFor(kind);
                return (
                  <details
                    key={kind}
                    className="mb-3 rounded-md border border-border bg-card"
                    open={entries.length <= 10}
                  >
                    <summary className="cursor-pointer px-4 py-3">
                      <span
                        className={cn(
                          'text-sm font-medium',
                          tone === 'error' ? 'text-tres-error' : 'text-tres-text-primary',
                        )}
                      >
                        {guide.title}
                      </span>{' '}
                      <span className="text-xs text-muted-foreground">
                        · {entries.length.toLocaleString()} item(s)
                      </span>
                    </summary>
                    <div className="border-t border-border px-4 py-3">
                      <p className="text-xs text-muted-foreground">{guide.explain}</p>
                      <ul className="mt-2 space-y-1 text-xs">
                        <li>
                          <span className="font-medium">If the data is wrong:</span>{' '}
                          {guide.fixData}
                        </li>
                        <li>
                          <span className="font-medium">If the data is right:</span>{' '}
                          {guide.fixSchema}
                        </li>
                      </ul>
                      <ul className="mt-3 space-y-2">
                        {entries.map((e, i) => (
                          <li key={i} className="rounded border border-border/60 p-2">
                            {e.entities.length > 0 && (
                              <p className="flex flex-wrap gap-2">
                                {e.entities.map((t) => (
                                  <EntityLink key={t} term={t} label={m.labels?.[t]} />
                                ))}
                              </p>
                            )}
                            <p className="mt-1 text-xs text-tres-text-primary">{e.what}</p>
                            {e.facts.length > 0 && (
                              <div className="mt-1 overflow-x-auto">
                                {e.facts.map((f, j) => (
                                  <p key={j} className="whitespace-nowrap font-mono text-[11px] text-muted-foreground">
                                    <EntityLink term={f.subject} label={m.labels?.[f.subject]} /> {f.predicate} {f.object}
                                  </p>
                                ))}
                              </div>
                            )}
                            {e.suggestion && (
                              <p className="mt-1 text-[11px] text-muted-foreground">{e.suggestion}</p>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </details>
                );
              })}
            </section>
          ),
      )}
    </div>
  );
}

function groupByKind(entries: DataReviewEntry[]): [string, DataReviewEntry[]][] {
  const groups = new Map<string, DataReviewEntry[]>();
  for (const e of entries) {
    const list = groups.get(e.kind) ?? [];
    list.push(e);
    groups.set(e.kind, list);
  }
  return [...groups.entries()];
}
